import { observer } from "mobx-react-lite";
import React, { useContext, useEffect } from "react";
import { useLocation } from "react-router-dom";

import HouseFilters from "../components/HouseFilters";
import HouseTypeSelector from "../components/HouseTypeSelector";
import Select, { SelectOption } from "../components/Select";
import TextInput from "../components/TextInput";
import { ApiContext, NavigationContext, UserContext } from "../contexts";
import { IS_DEV } from "../resources/constants";
import { getPageTitle } from "../resources/routes";
import { HouseType } from "../types";
import { scrollToTop } from "../util";

const PackageSearchScreen = observer(() => {
  const apiContext = useContext(ApiContext);
  const userContext = useContext(UserContext);
  const navContext = useContext(NavigationContext);
  const location = useLocation();

  useEffect(() => {
    scrollToTop();
    navContext.setFooterNavVisible(true);
    navContext.setNextButtonVisible(true);
    navContext.setLoginButtonVisible(false);
    navContext.setBackButtonVisible(true);
    navContext.setRestartButtonVisible(true);

    navContext.resetNextBtn();
    navContext.setNextButtonEnabled(IS_DEV);

    if (apiContext.enteredViaHome) {
      apiContext.setHouseType(apiContext.enteredViaHome.house_type);
    }

    navContext.setOnNavigateBack(() => {
      // Reset the form values when the user goes back
      apiContext.setHouseType(null);
      apiContext.resetLandFilters();
      apiContext.resetHouseFilters();
    });
  }, []);

  useEffect(() => {
    // When the form has been filled, enable the next button
    if (apiContext.region !== null) {
      navContext.setNextButtonEnabled(true);
    } else {
      navContext.setNextButtonEnabled(IS_DEV);
    }
  }, [apiContext.region, apiContext.estate, apiContext.estateOther]);

  const getRegionOptions = () => {
    const options = [{ key: "", value: "Please select region" }];
    for (const region of apiContext.regionResults) {
      options.push({
        key: region.id,
        value: region.title,
      });
    }
    return options;
  };

  const getEstateOptions = () => {
    const options = [{ key: "", value: "Please select estate" }];

    if (apiContext.region !== null) {
      for (const estate of apiContext.region.estates) {
        options.push({
          key: estate.id,
          value: estate.title,
        });
      }
    }

    options.push({ key: "other", value: "Other" });
    return options;
  };

  const onChangeRegion = (regionId: string) => {
    if (apiContext.region !== null && apiContext.region.id !== regionId) {
      apiContext.setEstate(null);
    }
    apiContext.setRegion(apiContext.regionsKeyedById[regionId]);
  };

  const onChangeEstate = (estateId: string) => {
    if (apiContext.region !== null && estateId !== "other") {
      apiContext.setEstateOther("");
      for (const estate of apiContext.region.estates) {
        if (estate.id === estateId) {
          apiContext.setEstate(estate);
        }
      }
    } else if (estateId === "other") {
      apiContext.setEstate({
        id: "other",
        title: "Other",
        site_cost: null,
      });
    }
  };

  const onClickHouseType = (option: HouseType) => {
    apiContext.toggleHouseType(option);
  };

  const getMinBudgetOptions = () => {
    const options: SelectOption[] = [
      {
        key: "",
        value: "Minimum",
      },
    ];
    for (const i of apiContext.hlPricesResults) {
      options.push({
        key: i,
        value: "$" + parseInt(i, 10).toLocaleString(),
      });
    }
    return options;
  };

  const getMaxBudgetOptions = () => {
    const options: SelectOption[] = [
      {
        key: "",
        value: "Maximum",
      },
    ];
    for (const i of apiContext.hlPricesResults) {
      options.push({
        key: i,
        value: "$" + parseInt(i, 10).toLocaleString(),
      });
    }
    return options;
  };

  return (
    <div className={"screen screen-package-search container"}>
      <h2 className="h1">{getPageTitle(location.pathname)}</h2>
      <p className={"subtitle"}>
        Please tell us about your block of land. This will allow us to suggest
        suitable home designs.
      </p>
      <form className={"your-land-form"}>
        <p className={"section-label"}>Where would you like to live?</p>
        <div className={"form-group"}>
          <Select
            id={"select-region"}
            options={getRegionOptions()}
            value={apiContext.region ? apiContext.region.id : ""}
            onChange={onChangeRegion}
          />
        </div>
        <div className={"form-group"}>
          <Select
            id={"select-estate"}
            options={getEstateOptions()}
            value={apiContext.estate ? apiContext.estate.id : ""}
            onChange={(val) => onChangeEstate(val)}
          />
          <TextInput
            id={"other"}
            disabled={
              apiContext.estate ? apiContext.estate.id !== "other" : true
            }
            placeholder={"(If Other) Please Specify"}
            value={apiContext.estateOther}
            onChange={(value) => apiContext.setEstateOther(value)}
            onScreenKeyboard={userContext.isKioskMode}
          />
        </div>
      </form>
      <p className={"section-label"}>Select House Type</p>
      <HouseTypeSelector
        onClick={onClickHouseType}
        selected={apiContext.houseType}
      />

      <div className={"filters-container"}>
        <div className={"filters-section buttons"}>
          <p className={"section-label"}>Select Accommodation Requirements</p>
          <HouseFilters
            beds={apiContext.numBeds}
            living={apiContext.numLiving}
            baths={apiContext.numBaths}
            cars={apiContext.numCars}
            onClickBeds={(val) => apiContext.toggleNumBeds(val)}
            onClickLiving={(val) => apiContext.toggleNumLiving(val)}
            onClickBaths={(val) => apiContext.toggleNumBaths(val)}
            onClickCars={(val) => apiContext.toggleNumCars(val)}
            onScreenKeyboard={userContext.isKioskMode}
          />
        </div>

        <div className={"filters-section budget"}>
          <p className={"section-label"}>
            What is your total House & Land budget?
          </p>
          <div className={"filters"}>
            <div className={"filter-section"}>
              <Select
                id={"select-min-budget"}
                options={getMinBudgetOptions()}
                value={apiContext.minBudget.toString(10)}
                onChange={(val) => apiContext.setMinBudget(parseInt(val, 10))}
              />
            </div>

            <div className={"filter-section"}>
              <Select
                id={"select-max-budget"}
                options={getMaxBudgetOptions()}
                value={apiContext.maxBudget.toString(10)}
                onChange={(val) => apiContext.setMaxBudget(parseInt(val, 10))}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

export default PackageSearchScreen;
